<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { RouterLink } from 'vue-router'
import { Star, User, Settings, LogOut } from 'lucide-vue-next'
import { useAuthStore } from '@/store/AuthStore'
import { apiClient } from '@/api'

/**
 * Account dropdown — user identity + profile / plan / sign-out. Shared by the
 * desktop rail account row and the mobile top-bar avatar button. Positioned by
 * the parent; `placement` only flips the corner radius emphasis.
 */
defineProps<{ placement?: 'up' | 'down' }>()
const emit = defineEmits<{ close: [] }>()

const router = useRouter()
const auth = useAuthStore()

const userName = computed(() => auth.userName)
const userEmail = computed(() => auth.currentUser?.email || '')
const isPro = computed(() => auth.isPro)

async function logout() {
  emit('close')
  try {
    await apiClient.post('/auth/logout')
  } catch {
    // Clear local state even if the server call fails.
  }
  auth.logout()
  await router.push('/login')
}
</script>

<template>
  <div class="account-menu" :class="`account-menu--${placement ?? 'up'}`">
    <div class="account-menu__head">
      <div class="account-menu__name-row">
        <span class="account-menu__name">{{ userName }}</span>
        <span v-if="isPro" class="account-menu__pro"><Star :size="8" /> PRO</span>
      </div>
      <span class="account-menu__email">{{ userEmail }}</span>
    </div>

    <div class="account-menu__group">
      <template v-if="auth.isTeacher">
        <RouterLink to="/settings" class="account-menu__item" @click="emit('close')">
          <User :size="16" /> Profile &amp; account
        </RouterLink>
        <RouterLink
          :to="isPro ? '/settings/membership' : '/settings/upgrade'"
          class="account-menu__item"
          @click="emit('close')"
        >
          <Star :size="16" /> {{ isPro ? 'Manage plan' : 'Upgrade to Pro' }}
        </RouterLink>
        <RouterLink to="/settings" class="account-menu__item" @click="emit('close')">
          <Settings :size="16" /> Settings
        </RouterLink>
      </template>
      <RouterLink v-else to="/account" class="account-menu__item" @click="emit('close')">
        <User :size="16" /> Account
      </RouterLink>
    </div>

    <div class="account-menu__group account-menu__group--last">
      <button type="button" class="account-menu__item account-menu__item--danger" @click="logout">
        <LogOut :size="16" /> Sign out
      </button>
    </div>
  </div>
</template>

<style scoped>
.account-menu {
  width: 240px;
  background: var(--surface-raised);
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-raise);
  overflow: hidden;
  font-family: var(--font-sans);
}
.account-menu__head {
  padding: 12px 14px;
  border-bottom: 1px solid var(--hairline);
}
.account-menu__name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.account-menu__name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account-menu__pro {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  background: var(--coral-soft);
  color: var(--coral);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
}
.account-menu__email {
  display: block;
  margin-top: 1px;
  font-size: 12px;
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account-menu__group {
  padding: 6px;
  border-bottom: 1px solid var(--hairline);
}
.account-menu__group--last {
  border-bottom: 0;
}
.account-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 8px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-sans);
  font-size: 13.5px;
  text-align: left;
  cursor: pointer;
  transition: background var(--motion-fast);
}
.account-menu__item:hover {
  background: var(--ink-100);
}
.account-menu__item--danger {
  color: var(--danger-ink);
}
</style>
